import type { CompanyCardDto, ContractSummaryDto, ObligationRegistryDto, PlantDto } from '@bk/contracts';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTenantQuery } from '../../../lib/useTenant';

const ore = (n: number) => `${n.toLocaleString('it-IT', { maximumFractionDigits: 1 })} ore`;

/**
 * Il documento scaricabile dell'assetto organizzativo (contratto n. 1, art. 5; ADR 0161): dati dell'impresa, sedi,
 * obblighi con la norma, organigramma per sede e reparto, processi, programmi, diagnosi, piano degli obiettivi, fonti e
 * la nota di responsabilità (art. 10.1). Si apre a tutta pagina e si stampa o si salva in PDF dal browser: niente file
 * prodotti sul server, sempre uguale a ciò che la pagina mostra.
 */
export function Documento() {
  const [aperto, setAperto] = useState(false);
  const card = useTenantQuery<CompanyCardDto>(['company'], '/company', { enabled: aperto });
  const plant = useTenantQuery<PlantDto>(['onboarding', 'plant'], '/onboarding/plant', { enabled: aperto });
  const obblighi = useTenantQuery<ObligationRegistryDto>(['onboarding', 'obblighi'], '/onboarding/obblighi', { enabled: aperto });
  const contratto = useTenantQuery<ContractSummaryDto>(['onboarding', 'contratto'], '/onboarding/contratto', { enabled: aperto });
  const pronto = card.data && plant.data && obblighi.data && contratto.data;
  return (
    <>
      <button className="rounded-xl bg-[#0d1220] px-4 py-2 text-sm text-white" onClick={() => setAperto(true)}>
        Scarica il documento dell’assetto organizzativo
      </button>
      {aperto &&
        createPortal(
          <div className="bk-documento fixed inset-0 z-50 overflow-y-auto bg-white text-[#1d1d1b]">
            <style>{`@media print { body > *:not(.bk-documento) { display: none !important; } .bk-documento { position: static !important; overflow: visible !important; } .bk-no-print { display: none !important; } } .bk-documento h2 { font-size: 18px; font-weight: 600; margin: 28px 0 8px; } .bk-documento h3 { font-size: 15px; font-weight: 600; margin: 14px 0 4px; } .bk-documento td, .bk-documento th { border-bottom: 1px solid #e4e1d9; padding: 4px 6px; text-align: left; vertical-align: top; font-size: 13px; }`}</style>
            <div className="bk-no-print sticky top-0 flex justify-end gap-2 border-b border-black/10 bg-white/95 px-6 py-3">
              <button className="rounded-xl bg-accent px-4 py-2 text-sm text-white disabled:opacity-40" disabled={!pronto} onClick={() => window.print()}>
                Stampa o salva in PDF
              </button>
              <button className="rounded-xl px-4 py-2 text-sm ring-1 ring-black/10" onClick={() => setAperto(false)}>
                Chiudi
              </button>
            </div>
            {!pronto ? (
              <p className="p-8 text-sm text-ink/60">Preparo il documento…</p>
            ) : (
              <article className="mx-auto max-w-3xl px-6 py-8 text-sm leading-relaxed">
                <h1 className="text-2xl font-semibold">L’assetto organizzativo ideale</h1>
                <p className="text-ink/60">
                  {card.data!.identita.ragione_sociale ?? plant.data!.impresa} · partita IVA {card.data!.identita.partita_iva ?? '—'} · documento del {new Date().toLocaleDateString('it-IT')}
                </p>
                <p className="mt-2">
                  Come dovrebbe essere organizzata l’azienda al suo meglio, secondo la legge e la buona prassi del settore, in proporzione alla sua natura e alla sua dimensione (art. 2086 c.c.). Ogni voce ha la sua fonte.
                </p>

                <h2>L’impresa</h2>
                <table className="w-full">
                  <tbody>
                    {[
                      ['Forma giuridica', card.data!.identita.forma_giuridica],
                      ['Attività prevalente', card.data!.identita.ateco_primario],
                      ['Sede legale', plant.data!.sedi.find((s) => s.legale)?.indirizzo ?? null],
                      ['Addetti', card.data!.identita.addetti],
                      ['Fatturato', card.data!.identita.fatturato ? `${Number(card.data!.identita.fatturato).toLocaleString('it-IT')} €` : null],
                    ]
                      .filter(([, x]) => x)
                      .map(([k, x]) => (
                        <tr key={String(k)}>
                          <th className="w-48 font-normal text-ink/60">{k}</th>
                          <td>{String(x)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {card.data!.cariche.filter((c) => c.stato === 'in_carica').length > 0 && (
                  <p className="mt-2">
                    Cariche: {card.data!.cariche.filter((c) => c.stato === 'in_carica').map((c) => `${c.carica} ${c.titolare}`).join('; ')}.
                  </p>
                )}

                <h2>Sedi e organigramma</h2>
                {plant.data!.sedi.map((s) => (
                  <div key={s.id}>
                    <h3>
                      {s.nome}
                      {s.indirizzo ? ` — ${s.indirizzo}` : ''}
                      {s.addetti != null ? ` · ${s.addetti} addetti` : ''}
                    </h3>
                    {s.organi.length > 0 && <p className="text-ink/70">Organi: {s.organi.map((o) => o.nome).join(', ')}</p>}
                    <table className="w-full">
                      <tbody>
                        {s.reparti.map((r) => (
                          <tr key={r.id}>
                            <th className="w-48">{r.nome}</th>
                            <td>
                              {r.ruoli.map((x) => `${x.nome}${x.persone ? ` (${x.persone})` : ''}`).join(', ')}
                              {r.programmi.length ? <span className="block text-ink/60">Programmi: {r.programmi.join(', ')}</span> : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}

                {contratto.data!.processi.length > 0 && (
                  <>
                    <h2>Processi</h2>
                    {contratto.data!.processi.map((p) => (
                      <p key={`${p.tipo}:${p.nome}`}>
                        <b>{p.nome}</b> ({p.tipo === 'primario' ? 'per i clienti' : 'di supporto'}{p.responsabile ? `, ne risponde ${p.responsabile}` : ''}): {p.fasi.map((f) => f.nome).join(' → ')}
                      </p>
                    ))}
                  </>
                )}

                {contratto.data!.programmi.length > 0 && (
                  <>
                    <h2>Programmi e piattaforme</h2>
                    <table className="w-full">
                      <tbody>
                        {contratto.data!.programmi.map((p) => (
                          <tr key={`${p.nome}|${p.dove}`}>
                            <th className="w-48 font-normal">{p.servizio ?? p.nome}</th>
                            <td>
                              {p.famiglia === 'piattaforma_pubblica' ? 'piattaforma pubblica' : p.famiglia === 'processi' ? 'processi' : 'ogni giorno'}
                              {p.norma ? ` · ${p.norma}` : ''}
                              {p.risponde ? ` · ne risponde ${p.risponde}` : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                <h2>Obblighi di legge</h2>
                <table className="w-full">
                  <thead>
                    <tr>
                      <th>Obbligo</th>
                      <th>Norma</th>
                      <th>Ogni quanto</th>
                      <th>Chi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {obblighi.data!.obblighi.map((o) => (
                      <tr key={o.chiave}>
                        <td>{o.cosa}</td>
                        <td>{o.norma}</td>
                        <td>{o.cadenza ?? '—'}</td>
                        <td>{o.chi ?? 'da assegnare'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {(contratto.data!.diagnosi.azienda.length > 0 || contratto.data!.diagnosi.reparti.some((r) => r.scarti.length)) && (
                  <>
                    <h2>Dove l’azienda è lontana dall’ideale</h2>
                    <ul className="list-disc pl-5">
                      {contratto.data!.diagnosi.azienda.map((g, i) => (
                        <li key={i}>{g.testo}</li>
                      ))}
                      {contratto.data!.diagnosi.reparti.flatMap((r) => r.scarti.map((g, i) => <li key={`${r.reparto_id}-${i}`}>{r.reparto}: {g.testo}</li>))}
                    </ul>
                  </>
                )}

                {contratto.data!.piano.length > 0 && (
                  <>
                    <h2>Gli obiettivi proposti</h2>
                    <ol className="list-decimal pl-5">
                      {contratto.data!.piano.map((o) => (
                        <li key={o.id} className="mb-2">
                          <b>{o.titolo}</b> — entro {o.scadenza_giorni} giorni. Oggi: {o.partenza} Traguardo: {o.traguardo}. Si misura: {o.misura}.
                        </li>
                      ))}
                    </ol>
                    {contratto.data!.diagnosi.totale && (
                      <p>
                        In tutto: {ore(contratto.data!.diagnosi.totale.ore_mese)} al mese liberate
                        {contratto.data!.diagnosi.totale.euro_anno !== null ? `, circa ${contratto.data!.diagnosi.totale.euro_anno.toLocaleString('it-IT')} € l’anno` : ''}. Come si calcola: {contratto.data!.diagnosi.totale.formula}.
                      </p>
                    )}
                  </>
                )}

                <h2>Fonti e responsabilità</h2>
                <p>
                  Le fonti sono la visura camerale e il bilancio dal Registro Imprese, le pagine pubbliche in cui compare la partita IVA dell’azienda, i registri pubblici e le fonti ufficiali di settore (ISTAT, ESCO, INAPP, CNEL, norme vigenti). Ogni voce, nella pagina di Timonio, dice la sua.
                </p>
                <p className="mt-2 text-ink/70">
                  Timonio propone un assetto ideale con le fonti; non sostituisce il consulente del lavoro, l’RSPP, il commercialista o l’avvocato. Gli obblighi sono indicati con la norma; decide l’azienda.
                </p>
              </article>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
